import { websocketLogger as logger } from '../../utils/logger';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';

/**
 * Service for managing WebSocket connections for real-time monitoring
 */
class WebSocketService {
    constructor() {
        this.stompClient = null;
        this.connected = false;
        this.subscriptions = new Map();
        // CRITICAL FIX: Add heartbeat mechanism to detect dead connections
        this.heartbeatInterval = null;
        this.heartbeatTimeout = null;
        this.lastPongReceived = Date.now();
        this.HEARTBEAT_INTERVAL_MS = 30000; // Send ping every 30 seconds
        this.HEARTBEAT_TIMEOUT_MS = 10000; // Wait 10 seconds for pong
        this.reconnectAttempts = 0;
        this.MAX_RECONNECT_ATTEMPTS = 5;
    }

    /**
     * Connect to the WebSocket server
     * @returns {Promise} Promise that resolves when connected
     */
    connect() {
        return new Promise((resolve, reject) => {
            if (this.connected) {
                logger.debug('🔌 WebSocketService: Already connected, resolving immediately');
                resolve();
                return;
            }

            // Use environment variables to determine WebSocket URL
            // When using nginx reverse proxy, construct full URL with correct protocol
            // This ensures HTTPS pages use wss:// and HTTP pages use ws://
            const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
            const host = window.location.host; // includes port if present
            const wsUrl = `${protocol}//${host}/api/ws`;

            logger.debug(`🔌 WebSocketService: Attempting connection to: ${wsUrl}`);
            const socket = new SockJS(wsUrl);
            this.stompClient = Stomp.over(socket);
            // Enable debug logging temporarily to see what's happening
            this.stompClient.debug = (str) => {
                logger.debug('🔌 STOMP Debug:', str);
            };

            // Get authentication headers
            const getAuthHeaders = () => {
                const userJson = localStorage.getItem('user');
                if (userJson) {
                    try {
                        const user = JSON.parse(userJson);
                        if (user.token) {
                            return {
                                'Authorization': `Bearer ${user.token}`
                            };
                        }
                    } catch (e) {
                        logger.error('Error parsing user data for WebSocket auth:', e);
                    }
                }
                return {};
            };

            const authHeaders = getAuthHeaders();
            logger.debug('🔐 WebSocketService: Auth headers present:', !!authHeaders.Authorization);

            this.stompClient.connect(
                authHeaders,
                frame => {
                    logger.debug('✅ WebSocketService: Connected successfully:', frame);
                    this.connected = true;
                    this.reconnectAttempts = 0;
                    // CRITICAL FIX: Start heartbeat mechanism
                    this.startHeartbeat();
                    resolve();
                },
                error => {
                    logger.error('❌ WebSocketService: Connection Error:', error);
                    this.connected = false;
                    this.stopHeartbeat();
                    // CRITICAL FIX: Attempt reconnection with exponential backoff
                    this.attemptReconnect();
                    reject(error);
                }
            );
        });
    }

    /**
     * Start heartbeat mechanism to detect dead connections
     * @private
     */
    startHeartbeat() {
        logger.debug('💓 WebSocketService: Starting heartbeat mechanism');
        this.lastPongReceived = Date.now();

        // Clear any existing heartbeat
        this.stopHeartbeat();

        // Send ping every 30 seconds
        this.heartbeatInterval = setInterval(() => {
            if (this.connected && this.stompClient) {
                logger.debug('💓 Sending heartbeat ping...');
                try {
                    // Send a lightweight ping message
                    this.stompClient.send('/app/ping', {}, JSON.stringify({ timestamp: Date.now() }));

                    // Set timeout to check if we receive pong
                    this.heartbeatTimeout = setTimeout(() => {
                        const timeSinceLastPong = Date.now() - this.lastPongReceived;
                        if (timeSinceLastPong > this.HEARTBEAT_TIMEOUT_MS) {
                            logger.warn('💔 No pong received, connection may be dead');
                            this.handleDeadConnection();
                        }
                    }, this.HEARTBEAT_TIMEOUT_MS);
                } catch (error) {
                    logger.error('❌ Error sending heartbeat:', error);
                    this.handleDeadConnection();
                }
            }
        }, this.HEARTBEAT_INTERVAL_MS);

        // Subscribe to pong responses
        if (this.stompClient && this.connected) {
            this.stompClient.subscribe('/user/queue/pong', (message) => {
                logger.debug('💓 Received pong');
                this.lastPongReceived = Date.now();
                if (this.heartbeatTimeout) {
                    clearTimeout(this.heartbeatTimeout);
                }
            });
        }
    }

    /**
     * Stop heartbeat mechanism
     * @private
     */
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
        if (this.heartbeatTimeout) {
            clearTimeout(this.heartbeatTimeout);
            this.heartbeatTimeout = null;
        }
    }

    /**
     * Handle dead connection detection
     * @private
     */
    handleDeadConnection() {
        logger.warn('💔 Dead connection detected, attempting reconnection');
        this.disconnect();
        this.attemptReconnect();
    }

    /**
     * Attempt to reconnect with exponential backoff
     * @private
     */
    attemptReconnect() {
        if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
            logger.error('❌ Max reconnection attempts reached, giving up');
            return;
        }

        const backoffDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
        this.reconnectAttempts++;

        logger.debug(`🔄 Attempting reconnection #${this.reconnectAttempts} in ${backoffDelay}ms`);

        setTimeout(() => {
            logger.debug(`🔄 Reconnecting... (attempt ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS})`);
            this.connect()
                .then(() => {
                    logger.debug('✅ Reconnection successful');
                })
                .catch((error) => {
                    logger.error('❌ Reconnection failed:', error);
                });
        }, backoffDelay);
    }

    /**
     * Disconnect from the WebSocket server
     */
    disconnect() {
        if (this.stompClient && this.connected) {
            // CRITICAL FIX: Stop heartbeat when disconnecting
            this.stopHeartbeat();

            // Unsubscribe all
            this.subscriptions.forEach(sub => {
                if (sub) {
                    sub.unsubscribe();
                }
            });
            this.subscriptions.clear();

            this.stompClient.disconnect();
            this.connected = false;
            logger.debug('WebSocket Disconnected');
        }
    }

    /**
     * Subscribe to a topic
     * @param {string} topic The topic to subscribe to
     * @param {function} callback The callback function
     * @returns {string} Subscription ID
     */
    subscribe(topic, callback) {
        const subscriptionId = `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        if (!this.connected) {
            logger.warn(`🔌 WebSocketService: Not connected, subscription to ${topic} will be delayed`);
            this.connect().then(() => {
                this._doSubscribe(topic, callback, subscriptionId);
            }).catch(error => {
                logger.error(`❌ WebSocketService: Failed to connect for subscription to ${topic}:`, error);
            });
        } else {
            this._doSubscribe(topic, callback, subscriptionId);
        }

        return subscriptionId;
    }

    /**
     * Internal method to handle subscriptions
     */
    _doSubscribe(topic, callback, subscriptionId) {
        logger.debug(`🔌 WebSocketService: Subscribing to topic: ${topic} with ID: ${subscriptionId}`);
        const subscription = this.stompClient.subscribe(topic, message => {
            logger.debug(`📨 WebSocketService: Received message on topic ${topic}:`, message);
            try {
                const payload = JSON.parse(message.body);
                logger.debug(`📨 WebSocketService: Parsed payload:`, payload);
                callback(payload);
            } catch (e) {
                logger.error(`❌ WebSocketService: Error parsing message on topic ${topic}:`, e, message);
            }
        });

        this.subscriptions.set(subscriptionId, subscription);
        logger.debug(`✅ WebSocketService: Successfully subscribed to ${topic} with ID: ${subscriptionId}`);
    }

    /**
     * Unsubscribe from a topic
     * @param {string} subscriptionId The subscription ID
     */
    unsubscribe(subscriptionId) {
        const subscription = this.subscriptions.get(subscriptionId);
        if (subscription) {
            subscription.unsubscribe();
            this.subscriptions.delete(subscriptionId);
        }
    }

    /**
     * Send a message to an application destination
     * @param {string} destination The destination (e.g. /app/tl-repl/input)
     * @param {object} body The message body
     */
    send(destination, body) {
        if (!this.connected || !this.stompClient) {
            logger.warn(`🔌 WebSocketService: Not connected, cannot send to ${destination}`);
            return;
        }
        this.stompClient.send(destination, {}, JSON.stringify(body));
    }

    /**
     * Subscribe to execution status updates
     * @param {string} executionId The execution ID
     * @param {function} callback The callback function
     * @returns {string} Subscription ID
     */
    subscribeToExecutionStatus(executionId, callback) {
        return this.subscribe(`/topic/executions/${executionId}/status`, callback);
    }

    /**
     * Subscribe to execution logs
     * @param {string} executionId The execution ID
     * @param {function} callback The callback function
     * @returns {string} Subscription ID
     */
    subscribeToExecutionLogs(executionId, callback) {
        return this.subscribe(`/topic/executions/${executionId}/logs`, callback);
    }

    /**
     * Subscribe to pipeline execution status updates
     * @param {string} pipelineId The pipeline ID
     * @param {function} callback The callback function
     * @returns {string} Subscription ID
     */
    subscribeToPipelineStatus(pipelineId, callback) {
        return this.subscribe(`/topic/pipelines/${pipelineId}/status`, callback);
    }

    /**
     * Subscribe to pipeline execution logs
     * @param {string} pipelineId The pipeline ID
     * @param {function} callback The callback function
     * @returns {string} Subscription ID
     */
    subscribeToPipelineLogs(pipelineId, callback) {
        return this.subscribe(`/topic/pipelines/${pipelineId}/logs`, callback);
    }

    /**
     * Subscribe to all execution status updates
     * @param {function} callback The callback function
     * @returns {string} Subscription ID
     */
    subscribeToAllExecutionStatus(callback) {
        return this.subscribe('/topic/executions/status', callback);
    }

    /**
     * Subscribe to file execution logs
     * @param {string} executionId The execution ID
     * @param {function} callback The callback function
     * @returns {string} Subscription ID
     */
    subscribeToFileExecutionLogs(executionId, callback) {
        return this.subscribe(`/topic/file-executions/${executionId}/logs`, callback);
    }

    /**
     * Subscribe to file execution progress
     * @param {string} executionId The execution ID
     * @param {function} callback The callback function
     * @returns {string} Subscription ID
     */
    subscribeToFileExecutionProgress(executionId, callback) {
        return this.subscribe(`/topic/file-executions/${executionId}/progress`, callback);
    }

    /**
     * Subscribe to file execution status
     * @param {string} executionId The execution ID
     * @param {function} callback The callback function
     * @returns {string} Subscription ID
     */
    subscribeToFileExecutionStatus(executionId, callback) {
        return this.subscribe(`/topic/file-executions/${executionId}/status`, callback);
    }

    /**
     * Subscribe to user notifications for real-time push
     * @param {string|number} userId The user ID
     * @param {function} callback The callback function receiving notification objects
     * @returns {string} Subscription ID
     */
    subscribeToNotifications(userId, callback) {
        return this.subscribe(`/topic/notifications/${userId}`, callback);
    }
}

// Create a singleton instance
const websocketService = new WebSocketService();
export default websocketService;
